import { TCNRRangeType } from "components/app/vessel/onboard/ranges/RangeManagementForm";
import { IAction, IListObjectStore } from "../../_common/type";
import { IForm, IFormStore } from "../../_core/form/types";
import { IJobHOCState, IJobGetStatus } from "../../_core/job/types";
import { ESealLogType } from "../logs/@types";
import vesselConstants, { TAB_IDS } from "./constants";
import { IInspectorStore, TLogbookPersonnel } from "./inspector/types";
import { IVesselOnboardStore } from "./onboard/types";
import { TMARPOLSettings } from "../marpol/@types";

type ITonnage = {
	grossTonnage: number;
	regGrossTonnage: number;
	deadWeight: number;
	netTonnage: number;
};
type IDimensions = {
	length: number;
	width: number;
	height: number;
	draft: number;
	loa: number;
};

type IHull = {
	dateofdelivery: string;
	length: number;
	builder: string;
	breadth: number;
	summerdeadweight: number;
	loadeddraught: number;
};

type IMainEngine = {
	maker: string;
	bore: string;
	type: string; //dropdown
	stroke: string;
	engineno: string;
	mcrbhpxrpm: number;
	numberofcylinders: number;
	ncrbhpxrpm: number;
};

type IPropeller = {
	manufacturer: string;
	diameter: string;
	type: string;
	stroke: string;
	material: string;
	constant: string;
};

type IAuxiliaryEngines = {
	numOfEngines: string;
	maker: string;
	bore: string;
	type: string;
	stroke: string;
	engineno: string;
	rpm: number;
	typeofgen: string;
	outputpowerkw: number;
};

type IAdminDetails = {
	dateOfInstall: string;
	activeModulesLogs: string;
	activeModulesMarpol: any;
	activeModulesChecklists: any;
	authorizedBy: string;
	soldDate: string;
	isActive: boolean;
	paperCutoverDate: string;
	bwrbInstallDate: string;
	vesselContactEmail: string;
};

type IVersionInfo = {
	FE: string;
	BE: string;
};

interface ILiveVersionInfo {
	FE: string;
	BE: string;
	config: string;
}

interface IDesignation {
	type: string;
	label: string;
	rank: number;
	name: string;
	vesselId: string;
	id: string;
}

interface ILiveLastUpdatedBy {
	name: string;
	id: string;
	designation: IDesignation;
}
interface ILiveInfoDetails {
	local_reading: string;
	lastUpdatedBy: ILiveLastUpdatedBy;
}

interface ILiveInfoTo extends ILiveInfoDetails {}
interface ILiveInfoFrom extends ILiveInfoDetails {}
interface IStateOfShip extends ILiveInfoDetails {}

type ILiveInfo = {
	versionInfo: ILiveVersionInfo;
	last_synced: string;
	from: ILiveInfoTo;
	to: ILiveInfoFrom;
	state_of_ship: IStateOfShip;
	last_location?: {
		lat: string;
		long: string;
	};
};

export interface IUsabilityAlertDateObj {
	watchHour?: number[];
	isApprove?: boolean[];
	hint: string;
	state: string;
	status?: string[];
}

export enum EVESSEL_ALERTS_STATUS {
	GOOD = "GOOD",
	MEDIUM = "MEDIUM",
	HIGH = "HIGH",
}

export interface IVesselAlertDescriptor {
	description: string;
}

export type TVesselAlertsUsability = Record<string, IUsabilityAlertDateObj>;
export interface IVesselAlerts {
	vesselId: string;
	fromDate: string;
	toDate: string;
	usability: Record<string, TVesselAlertsUsability>;
	alerts: Record<string, TVesselAlertsUsability>;
	status: EVESSEL_ALERTS_STATUS;
	observations: {
		alerts: Record<string, IVesselAlertDescriptor[]>;
		usage: Record<string, IVesselAlertDescriptor[]>;
	};
}

export type IVessel = {
	vesselRefId?: string;
	vesselName: string;
	imoNumber: string;
	yearBuildDate: string;

	portOfRegistry: string;
	nationality: string;
	nameOfTheOwner?: string;
	flag?: string;
	vesselCode?: string;
	vesselPrefix?:
		| "MV"
		| "MT"
		| "AE"
		| "AFS"
		| "AHT"
		| "AHTS"
		| "AO"
		| "AOG"
		| "AOR"
		| "AOT"
		| "ATB"
		| "BRP"
		| "CRV"
		| "C/F"
		| "CS"
		| "DB"
		| "DEPV"
		| "DLB"
		| "DCV"
		| "DSV"
		| "DV"
		| "ERRV"
		| "EV"
		| "FPSO"
		| "FPV"
		| "FT"
		| "FV"
		| "GTS"
		| "HLV"
		| "HMHS"
		| "HSC"
		| "HSF"
		| "HTV"
		| "IRV"
		| "ITB"
		| "LB"
		| "LNG/C"
		| "LPG/C"
		| "MF"
		| "MFV"
		| "MS (M/S)"
		| "MSV"
		| "MSY"
		| "MT"
		| "MTS"
		| "MV"
		| "MY"
		| "NB"
		| "NRV"
		| "NS"
		| "OSV"
		| "PS"
		| "PSV"
		| "QSMV"
		| "QTEV"
		| "RMS"
		| "RNLB"
		| "RRS"
		| "RV/RSV"
		| "SB"
		| "SL"
		| "SS(S/S)"
		| "SSCV"
		| "SSS"
		| "SSV"
		| "ST"
		| "STS"
		| "STV"
		| "SV"
		| "SY"
		| "TB"
		| "TEV"
		| "TIV"
		| "TrSS"
		| "TS"
		| "Tr.SMV"
		| "TSMV"
		| "TSS"
		| "TST"
		| "TV"
		| "ULCC"
		| "VLCC"
		| "YD"
		| "YT"
		| "YMT"
		| "YTB"
		| "YTL"
		| "YTM"
		| "YW"
		| "YWN"
		| "YOS"
		| undefined;
	vesselType?: "Dry" | "Liquid" | undefined;
	vesselSubType?:
		| "General Cargo Carrier"
		| "Container Carrier"
		| "RO-RO Carrier"
		| "Bulk Carrier"
		| "Crude Carrier"
		| "Product Carrier"
		| "Liquefied Carrier"
		| "Chemical Carrier"
		| undefined;
	isExhauseGasCleanerScrubberProvided?: boolean;
	shipSizeCategory?:
		| "Small Handy Size"
		| "Seawaymax"
		| "Handy Size"
		| "Handy Max"
		| "Panamax"
		| "Neopanamax"
		| "Capsize"
		| "Chinamax"
		| "Aframax"
		| "Q-Max"
		| "Suezmax"
		| "VLCC"
		| "ULCC"
		| "Malaccamax"
		| undefined;

	vesselFuels?: any;

	addressOfTheOwner?: string;
	callSign?: string;
	tonnage?: ITonnage;
	mmsiNumber?: string;
	distinctiveNumberOrLetters?: string;
	dimensions?: IDimensions;
	hull?: IHull;
	mainengine?: IMainEngine;
	propeller?: IPropeller;
	shaftGenerator?: any;
	auxiliaryengines?: IAuxiliaryEngines;
	id: string;
	image?: string;
	tenantId?: string;
	createdAt?: string;
	updatedAt?: string;
	isCrewOnboarded?: boolean;
	isDeviceOnboarded?: boolean;
	liveInfo?: ILiveInfo;
	vesselAlerts?: IVesselAlerts;
	adminDetails?: IAdminDetails;
};

export type IVesselCrew = {
	name: string;
	fromDate?: string;
	toDate?: string;
	designation: string;
	type?: string;
	userId?: string;
	staffId: string;
	// image: string;
	vesselId?: string;
	id: string;
	label?: string;
	rank?: number;
};

export type IVesselDevice = {
	id: string;
	deviceType: "TABLET" | "DESKTOP";
	operatingSystem: string;
	deviceName: string;
	version: string;
	macAddress: string;
	deviceId: string;
	vesselId?: string;
};

export type IVesselFilters = {
	imoNumber?: string;
	vesselName?: string;
	vesselType?: "Dry" | "Liquid" | "";
	nameOfTheOwner?: string;
};

export interface IVesselFilterForm
	extends IForm<IVesselFilters, any, any, any> {}

// All Actions below
export type IVessel_ListRemove__Action = IAction<{
	vesselId: string;
}>;
export type IVessel_ListLoad__Action = IAction<any>;
export type IVessel_ListFilter__Action = IAction<IVesselFilters>;
export type IVessel_ListSet__Action = IAction<IListObjectStore<IVessel>>;
export type IVessel_ListInfoSet__Action = IAction<IListObjectStore<IVessel>>;
export type IVessel_ListReset__Action = IAction<any>;
export type IVessel_ListPush__Action = IAction<IVessel>;
export type IVessel_listEdit__Action = IAction<{
	vesselId: string;
	vessel: IVessel;
}>;
export type ITab_Select = IAction<{ tabSelected: TAB_IDS }>;
export type IVessel_FavoriteSet__Action = IAction<{
	vesselIds: string[];
}>;
export type IFleet_ListSet__Action = IAction<{ fleets: IFleet[] }>;
export type IFleet_IdSet__Action = IAction<{ fleetId: string }>;
export type IEnable_VesselSelection__Action = IAction<{ isSelected: boolean }>;
export type ISelectedVessel_Add__Action = IAction<{ vesselId: string }>;
export type IFleetName_Add__Action = IAction<{ fleetName: string }>;
export type IFleetDropdown_Toggle__Action = IAction<{ isOpen: boolean }>;
export type IVesselCard_OperationAdd__Action = IAction<{
	operation: TVesselCardOperation;
}>;

// All Selectors below
export type IVessel_List__Get = {
	vessels: IListObjectStore<IVessel>;
	listLoadJob: IJobGetStatus;
	onboardStatus: {
		totalVessels: number;
		onboardedVessels: number;
	};
};

export interface IFleet {
	_id: string;
	_rev: string;
	name: string;
	tenantId: string;
	modelName: string;
	createdAt: string;
}

export type TVesselCardOperation = "fav" | "unfav";
export type TPossibleOperations = "fav" | "unfav" | "fav/unfav";

// Vessel Store
export interface IVesselStore extends IFormStore, IJobHOCState {
	vesselsInfo: IListObjectStore<IVessel>;
	vessels: IListObjectStore<IVessel>;
	myVessels: IListObjectStore<IVessel>;
	_forms: {
		FILTER: IVesselFilterForm;
	};
	onboard: IVesselOnboardStore;
	inspector: IInspectorStore;
	selectedTab: string;
	favoriteVesselIds: string[];
	fleets: IFleet[];
	selectedFleetId: string;
	enableSelection: boolean;
	selectedVesselsOnEnableSelection: string[];
	selectedFleetIdToWhichVesselsToBeAssigned: string;
	fleetName: string;
	isCreateFleetModalOpen: boolean;
	isFleetListDropdownOpen: boolean;
	isAssignVesselsToFleetDropdownOpen: boolean;
	// vessel operation can be favoriting or unfavoriting, this accumulates list of favoriting & unfavoriting done prior to api call.
	operationsPriorToDebounce: TVesselCardOperation[];
    globalSettings?: TVesselGlobalLog;
    logbookPersonnels: IListObjectStore<TLogbookPersonnel>;
	portablePumps: IPumps[];
	flexibleHoses: IHoses[];
}

export interface IUserDetail {
	name: string;
	role: string;
	roleCategory: string;
	staffId: string;
	lastRelievingDay: string;
	isRelieved: boolean;
	email: string;
	password: string;
	id: string;
	tenantId: string;
	createdAt: string;
	updatedAt: string;
	_rev: string;
	username: string;
	favourite_vessels: string[];
}

export interface IVesselOperation {
	vesselId: string | null;
	operation: "ADD_VESSEL" | "UPDATE_VESSEL";
	isModalOpen: boolean;
}

export interface ICreateFleetResponse {
	ok: boolean;
	id: string;
	rev: string;
}

export interface IDashboardStatusType {
	title: string;
	status: string;
	color: string;
}

export enum EBookType {
    LOG = 'LOG',
    CHECKLIST = 'CHECKLIST',
    PERMIT = 'PERMIT',
    MARPOL = 'MARPOL',
}

export interface IBook {
    title: string;
    id: string;
    type: string;
    bookType: EBookType;
}

export interface IEntry {
    id: string;
    title: string;
    typeOfLogs: string;
    type: string;
	bookType?: string;
}

export enum EMarpolBookId {
    ORB1 = 'ORB1',
    ORB4 = 'ORB4',
    CRB = 'CRB',
    GRB1 = 'GRB1',
    GRB2 = 'GRB2',
    BFL2 = 'BFL2',
    BFL1 = 'BFL1',
    ANNEX6 = 'ANNEX6',
    BALLAST = 'BALLAST',
    BALLAST_TANK_OPERATIONS = 'BALLAST_TANK_OPERATIONS',
}

export interface ISealLogsLocation {
    id: string;
    name: string;
    type: ESealLogType,
    mandatory?: boolean;
    remarks?: string;
    modifiedAt?: Date;
    attachment?: File;
    sub_locations?: string[];
}

export type TSealLogsLocations = {
    defaultLocations?: ISealLogsLocation[];
    customLocations?: ISealLogsLocation[];
};

export type TLogbookTank = {
    id: string;
    name: string;
};

export type TBallastShipDiagramAttachmentType = {
    name: string;
    id?: string;
};

export enum AuthDesignation {
    ANY = 'ANY',
    CAPTAIN = 'MASTER',
    CHIEF_OFFICER = 'CO',
    CHIEF_ENGINEER = 'CE',
    FIRST_ENGINEER = '1AE',
    SECOND_ENGINEER = '2AE',
    THIRD_ENGINEER = '3AE',
    FIRST_OFFICER = '1AO',
    SECOND_OFFICER = '2AO',
}

export type TMedicalOfficer = {
    id: string;
    staffId: string;
    name: string;
    rank: string;
    from: string;
    to: string;
};

export type TInjuryCategories = {
    default: TInjuryCategory[];
    list: TInjuryCategory[];
};

export type TInjuryCategory = {
    name: string;
    description: string;
    id: string;
    highlight: boolean;
};

export type TWeeklyStandardMessages = {
    default: TWeeklyStandardMessage[];
    list: TWeeklyStandardMessage[];
};

export type TWeeklyStandardMessage = {
    description: string;
    id: string;
};

export type TControlledDrugsRegister = {
    administeredOn: string;
    name: string;
    reason: string;
    drugName: string;
    drugQuantity: string;
    administeredBy: string;
    runningBalance: string;
    timezone: string;
    createdAt: string;
};

export type TVesselGlobalLog = {
    sealLogsLocations?: TSealLogsLocations;
    versionNumber: number;
    general: {
        vesselName: string;
        rv?: number;
        callSign: string;
        mmsiNumber: string;
        portOfRegistry: string;
        flag: string;
        imoNumber: string;
        grossRegisterTonnage: string;
        netRegisterTonnage: string;
        lengthOverall: string;
        dateKeelLaid: string;
        deadWeight: string;
        breadth: string;
        yearBuilt: string;
        depth: string;
        portAnchorShackles: string;
        starboardAnchorShackles: string;
        displacement: string;
        areasShipCertifiedToOperate: string;
        [key: string]: any;
    };
    gmddsRadioLogSectionA: {
        dateOfExpiryOfShipRadioCertificate: string;
        inmarsatA?: string;
        inmarsatB?: string;
        inmarsatC?: string;
        inmarsatD?: string;
        officialNo?: string;
        certifiedSeaAreaForShip?: string;
    };
    methodsOfRadioFacilities: {
        isDuplicationOfEquipment: boolean;
        isShoreBasedMaintainance: boolean;
        isAtSeaMaintainanceCapability: boolean;
        nameOfServiceCompany: string;
        addressOfServiceCompany: string;
        nameOfShipOwnerOrManager: string;
        addressOfShipOwnerOrManager: string;
    };
    localRanges?: {
        [key: string]:
            | {
                  metric: string;
                  range: [number, number];
                  rangeType?: TCNRRangeType;
                  remarks?: string;
                  isLocalRange?: boolean;
                  modifiedAt?: string;
                  type?: string; //Info: For signal-report local-ranges
              }
            | {
                  [key: string]: {
                      metric: string;
                      range: [number, number];
                      rangeType?: TCNRRangeType;
                      remarks?: string;
                      isLocalRange?: boolean;
                      modifiedAt?: string;
                      type?: string;
                  };
              };
    };
    orb1: {
        identityOfTanks: string;
        capacityOfTanks?: number;
        totalQuantityOfRetention?: number;
        quantityOfResidueCollectedByManualOperation?: number;
    };
    orb4: {
        tanks: TLogbookTank[];
    };
    ballast: {
        tanks: TLogbookTank[];
        bwtsMaker: string;
        bwtsModel: string;
        bwtsTechnology: string;
        totalBallastWaterCapacity?: number;
        isShipProvidedWithBWMP?: boolean; //is ship provided with ballast-water management plan
        isDiagramOfShipIndicatingBallastTankAttached?: boolean;
        shipDiagramAttachment?: TBallastShipDiagramAttachmentType;
        version: string; // MEPC80 changes
        lastByPassDate?: string;
        noOfBallastTanks?: number;
        bwmsMethods: string;
        contingencyMeasuresBwmsPlan?: string;
        operational: boolean;
        bwmsApproved: boolean;
        bwmsPlanImplemented: boolean;
        contingency: boolean;
        bwmsOnboard: boolean;
        internationalBwms: boolean;
        receptionFacility: boolean;
        authorityCertificate: string;
        certificateNo: string;
        dateOfIssue: string;
        expiryDate: string;
        placeOfIssue: string;
        surveyDate: string;
        surveyingAuthority: string;
    };
    GRB1?: {
        userForIncineratorSubmission: AuthDesignation;
        userForIncineratorVerification: AuthDesignation;
    };
    cnr?: {
        hiddenFields: {
            [configType: string]: {
                [fieldName: string]: {
                    isNotToBeValidated?: boolean;
                    isHidden?: boolean;
                    requiredFieldMessage?: string;
                };
            };
        };
    };
    // Had to create separate cnrSettings - because cnr above was initially kept optional. Changing it has a unintended impact.
    cnrSettings: {
      hasFuelPropertiesDropdown: boolean;
    },
    feedbackConfiguration?: {
        isFeedbackGlobal?: boolean;
        isWayshipAIChatEnabled?: boolean;
    };
    medical: {
        medicalOfficers: TMedicalOfficer[];
        injuryCategories: TInjuryCategories;
        weeklyStandardMessages: TWeeklyStandardMessages;
        controlledDrugsRegister: TControlledDrugsRegister[];
    };
    MARPOL?: TMARPOLSettings;
    [key: string]: any;
};

export type IPumps = {
    id?: string;
    id_number: string;
    type: string;
    createdAt?: Date;
    updatedAt?: Date;
};

export type IHoses = {
    id?: string;
    id_number: string;
    diameter: number | string;
    length: number | string;
    material: string;
    createdAt?: Date;
    updatedAt?: Date;
};